"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Layers, SlidersHorizontal as Sliders, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  id: "pat",
  title: "Process Analytical Technology",
  subtitle: "Real-Time Quality & Process Control",
  description: "In-line, on-line, and at-line analytical instrumentation designed to measure critical quality and performance attributes throughout manufacturing.",
  linkPath: "/ServicesPage",
  iconName: "activity",
  featureOne: "In-line & at-line spectroscopic monitoring",
  featureTwo: "Real-time process parameter verification",
  featureThree: "Complete regulatory qualification support"
}, {
  id: "blend-uniformity",
  title: "Blend Uniformity",
  subtitle: "High-Precision Powder & Mixing Analysis",
  description: "Specialized non-destructive monitoring solutions to determine blend homogeneity, minimize cycle times, and ensure batch-to-batch consistency.",
  linkPath: "/ServicesPage",
  iconName: "layers",
  featureOne: "Quantitative end-point blend detection",
  featureTwo: "Non-destructive contactless measurements",
  featureThree: "Seamless integration with production vessels"
}];
export default function ProcessServices() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="processservicesoverview" data-nav="light" className="bg-background py-16 sm:py-24 border-b border-border/40">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="size-3.5 text-primary" />
            <span>Process & Laboratory Services</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            Advanced Process Analytical Solutions
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Application-led analytical instrumentation and lifecycle support for regulated laboratories and pharmaceutical manufacturing facilities.
          </p>
        </div>

        {/* 2-Column Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, i) => <div key={service.id} data-index={i} className="h-full">
              <motion.div className="h-full flex flex-col" initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.5,
            delay: i * 0.15
          }}>
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 rounded-xl overflow-hidden group">
                  <div>
                    {/* Card Header & Accent */}
                    <CardHeader className="p-6 sm:p-8 pb-4">
                      <div className="flex items-center justify-between gap-4 mb-5">
                        <div className="size-12 rounded-lg bg-secondary flex items-center justify-center text-primary group-hover:scale-105 transition-transform duration-300">
                          {service.iconName === "activity" ? <Activity className="size-6" /> : <Layers className="size-6" />}
                        </div>
                        <span className="text-xs font-medium text-muted-foreground bg-muted/60 px-3 py-1 rounded-full">
                          Regulated Support
                        </span>
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                          {service.subtitle}
                        </p>
                        <CardTitle className="text-2xl sm:text-3xl font-default font-bold text-foreground">
                          {service.title}
                        </CardTitle>
                      </div>

                      <CardDescription className="text-muted-foreground text-sm sm:text-base mt-3 leading-relaxed">
                        {service.description}
                      </CardDescription>
                    </CardHeader>

                    {/* Features List */}
                    <CardContent className="p-6 sm:p-8 pt-2 pb-6">
                      <div className="border-t border-border/60 pt-5 space-y-3">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground font-medium">
                            {service.featureOne}
                          </span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground font-medium">
                            {service.featureTwo}
                          </span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground font-medium">
                            {service.featureThree}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </div>

                  {/* Card Actions */}
                  <CardFooter className="p-6 sm:p-8 pt-0 flex items-center justify-between border-t border-border/40 bg-secondary/20">
                    <Button asChild variant="default" size="default" className="w-full sm:w-auto font-medium">
                      <Link to={service.linkPath} className="flex items-center justify-center gap-2">
                        <span>Explore {service.title}</span>
                        <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Bottom Support Callout */}
        <div className="mt-12 p-6 sm:p-8 rounded-xl bg-secondary/50 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-background text-primary shrink-0 border border-border">
              <Sliders className="size-5" />
            </div>
            <div>
              <h3 className="font-default font-semibold text-foreground text-base sm:text-lg">
                Need customized qualification or method integration?
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Our application specialists provide full lifecycle technical support across Ireland and the UK.
              </p>
            </div>
          </div>
          <Button asChild variant="outline" size="default" className="shrink-0 w-full sm:w-auto">
            <Link to="/ContactPage">
              Speak with a Specialist
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}